import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { ArrowLeft, Download, FilePdf, FileXls, Plus, Package } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ModuleGuide, EmptyState } from '../components/GuidanceComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const MaterialTracking = () => {
  const { quoteId: urlQuoteId, projectId } = useParams();
  const navigate = useNavigate();
  
  const [selectedQuoteId, setSelectedQuoteId] = useState(urlQuoteId || '');
  const [projectQuotes, setProjectQuotes] = useState([]);
  const [orderList, setOrderList] = useState([]);
  const [trackingReport, setTrackingReport] = useState([]);
  const [inwards, setInwards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('order');
  
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [actualCost, setActualCost] = useState('');
  const [supplier, setSupplier] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().split('T')[0]);

  const quoteId = selectedQuoteId;

  useEffect(() => {
    if (projectId && !urlQuoteId) {
      // Control Tower route: load project quotes first
      loadProjectQuotes();
    } else if (urlQuoteId) {
      setSelectedQuoteId(urlQuoteId);
      fetchData(urlQuoteId);
    } else {
      setLoading(false);
    }
  }, [projectId, urlQuoteId]);

  const loadProjectQuotes = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/quotes/project/${projectId}`, { withCredentials: true });
      const lockedQuotes = res.data.filter((q) => q.revision > -1); // all quotes
      setProjectQuotes(lockedQuotes);
      if (lockedQuotes.length > 0) {
        setSelectedQuoteId(lockedQuotes[0].id);
        fetchData(lockedQuotes[0].id);
      } else {
        setLoading(false);
      }
    } catch {
      toast.error('Failed to load quotes');
      setLoading(false);
    }
  };

  const handleQuoteChange = (newQuoteId) => {
    setSelectedQuoteId(newQuoteId);
    fetchData(newQuoteId);
  };

  const fetchData = async (qid) => {
    const fetchId = qid || quoteId;
    if (!fetchId) return;
    try {
      const [orderRes, trackingRes, inwardsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/quotes/${fetchId}/material-order-list`, {
          withCredentials: true,
        }),
        axios.get(`${BACKEND_URL}/api/quotes/${fetchId}/material-tracking`, {
          withCredentials: true,
        }),
        axios.get(`${BACKEND_URL}/api/material-inwards/quote/${fetchId}`, {
          withCredentials: true,
        }),
      ]);
      setOrderList(orderRes.data);
      setTrackingReport(trackingRes.data);
      setInwards(inwardsRes.data);
    } catch (error) {
      toast.error('Failed to load material data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/quotes/${quoteId}/material-order-list/export/${format}`,
        {
          withCredentials: true,
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `MaterialReport.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    }
  };

  const handleRecordInward = async (e) => {
    e.preventDefault();
    
    if (!selectedMaterial) {
      toast.error('Please select a material');
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/material-inwards`,
        {
          quote_id: quoteId,
          material_description: selectedMaterial.description,
          specification: selectedMaterial.specification,
          make: selectedMaterial.make,
          quantity: parseFloat(quantity),
          unit: selectedMaterial.unit,
          actual_cost: parseFloat(actualCost),
          supplier,
          invoice_number: invoiceNumber,
          received_date: new Date(receivedDate).toISOString(),
        },
        { withCredentials: true }
      );
      
      toast.success('Material inward recorded successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record inward');
    }
  };

  const resetForm = () => {
    setSelectedMaterial(null);
    setQuantity('');
    setActualCost('');
    setSupplier('');
    setInvoiceNumber('');
    setReceivedDate(new Date().toISOString().split('T')[0]);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'green':
        return 'bg-green-100 text-green-800 border-green-400';
      case 'amber':
        return 'bg-yellow-100 text-yellow-800 border-yellow-400';
      case 'red':
        return 'bg-red-100 text-red-800 border-red-400';
      default:
        return 'bg-zinc-100 text-zinc-800 border-zinc-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'green':
        return '✓';
      case 'amber':
        return '⚠';
      case 'red':
        return '✕';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="animate-pulse text-zinc-600">Loading...</div>
      </div>
    );
  }

  const totalBudgeted = trackingReport.reduce((sum, item) => sum + item.budgeted_cost, 0);
  const totalActual = trackingReport.reduce((sum, item) => sum + item.actual_cost, 0);
  const totalVariance = totalActual - totalBudgeted;
  const totalVariancePct = totalBudgeted > 0 ? (totalVariance / totalBudgeted * 100) : 0;

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            onClick={() => projectId ? navigate(`/projects/${projectId}?tab=control`) : navigate(-1)}
            variant="outline"
            className="border-zinc-300 rounded-none hover:bg-zinc-100 mb-3"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">
            Material Tracking
          </h1>
          {projectQuotes.length > 1 && (
            <div className="mt-2 flex items-center gap-2">
              <Label className="text-xs font-bold uppercase text-zinc-500">Quote:</Label>
              <select value={selectedQuoteId} onChange={(e) => handleQuoteChange(e.target.value)}
                className="bg-zinc-50 border border-zinc-200 rounded-none p-1.5 text-xs" data-testid="quote-selector">
                {projectQuotes.map((q) => <option key={q.id} value={q.id}>{q.title} (₹{q.total?.toLocaleString('en-IN')})</option>)}
              </select>
            </div>
          )}
          <p className="text-sm text-zinc-600 mt-1">
            Order list, inward tracking, and budgeted vs actual report
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-zinc-200">
          <button
            onClick={() => setActiveTab('order')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'order'
                ? 'border-b-2 border-blue-700 text-blue-700'
                : 'text-zinc-600 hover:text-zinc-900'
            }`}
          >
            Order List ({orderList.length})
          </button>
          <button
            onClick={() => setActiveTab('tracking')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'tracking'
                ? 'border-b-2 border-blue-700 text-blue-700'
                : 'text-zinc-600 hover:text-zinc-900'
            }`}
          >
            Tracking Report
          </button>
          <button
            onClick={() => setActiveTab('inwards')}
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              activeTab === 'inwards'
                ? 'border-b-2 border-blue-700 text-blue-700'
                : 'text-zinc-600 hover:text-zinc-900'
            }`}
          >
            Inward History ({inwards.length})
          </button>
        </div>

        {/* Order List Tab */}
        {activeTab === 'order' && (
          <div>
            <ModuleGuide text="View all materials required for the project with budgeted costs. Record material inwards as deliveries arrive to track budgeted vs actual spending." />
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold tracking-tight text-zinc-800">
                Material Order List
              </h2>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleExport('pdf')}
                  variant="outline"
                  className="border-zinc-300 rounded-none hover:bg-zinc-100"
                >
                  <FilePdf size={20} className="mr-2" />
                  PDF
                </Button>
                <Button
                  onClick={() => handleExport('excel')}
                  variant="outline"
                  className="border-zinc-300 rounded-none hover:bg-zinc-100"
                >
                  <FileXls size={20} className="mr-2" />
                  Excel
                </Button>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-700 hover:bg-blue-800 text-white rounded-none">
                      <Plus size={20} className="mr-2" />
                      Record Inward
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-2xl">
                    <DialogHeader>
                      <DialogTitle className="text-2xl font-bold text-zinc-900">
                        Record Material Inward
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleRecordInward} className="space-y-4 mt-4">
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                          Select Material *
                        </Label>
                        <select
                          value={selectedMaterial?.description || ''}
                          onChange={(e) => {
                            const mat = orderList.find(m => m.description === e.target.value);
                            setSelectedMaterial(mat);
                          }}
                          required
                          className="mt-2 w-full bg-zinc-50 border border-zinc-200 focus:border-blue-600 rounded-none p-3"
                        >
                          <option value="">Select material</option>
                          {orderList.map((mat, idx) => (
                            <option key={idx} value={mat.description}>
                              {mat.description} {mat.specification ? `- ${mat.specification}` : ''} ({mat.make})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                            Quantity *
                          </Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                            required
                            className="mt-2 bg-zinc-50 border-zinc-200 rounded-none p-3"
                          />
                          {selectedMaterial && (
                            <p className="text-xs text-zinc-500 mt-1">
                              Budgeted: {selectedMaterial.quantity} {selectedMaterial.unit}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                            Actual Cost *
                          </Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={actualCost}
                            onChange={(e) => setActualCost(e.target.value)}
                            required
                            className="mt-2 bg-zinc-50 border-zinc-200 rounded-none p-3"
                            placeholder="₹"
                          />
                          {selectedMaterial && (
                            <p className="text-xs text-zinc-500 mt-1">
                              Budgeted: ₹{selectedMaterial.budgeted_cost.toFixed(2)}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                            Supplier
                          </Label>
                          <Input
                            value={supplier}
                            onChange={(e) => setSupplier(e.target.value)}
                            className="mt-2 bg-zinc-50 border-zinc-200 rounded-none p-3"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                            Invoice Number
                          </Label>
                          <Input
                            value={invoiceNumber}
                            onChange={(e) => setInvoiceNumber(e.target.value)}
                            className="mt-2 bg-zinc-50 border-zinc-200 rounded-none p-3"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                            Received Date *
                          </Label>
                          <Input
                            type="date"
                            value={receivedDate}
                            onChange={(e) => setReceivedDate(e.target.value)}
                            required
                            className="mt-2 bg-zinc-50 border-zinc-200 rounded-none p-3"
                          />
                        </div>
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-none py-6"
                      >
                        Record Inward
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            <div className="bg-white border border-zinc-200 overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-100 border-b border-zinc-200">
                  <tr>
                    <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">S.No</th>
                    <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Description</th>
                    <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Specification</th>
                    <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Make</th>
                    <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Unit</th>
                    <th className="text-right p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Quantity</th>
                    <th className="text-right p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Budgeted Cost</th>
                    <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Trade</th>
                  </tr>
                </thead>
                <tbody>
                  {orderList.map((item, idx) => (
                    <tr key={idx} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="p-3 text-sm">{idx + 1}</td>
                      <td className="p-3 text-sm font-medium">{item.description}</td>
                      <td className="p-3 text-sm text-zinc-600">{item.specification}</td>
                      <td className="p-3 text-sm text-zinc-600">{item.make}</td>
                      <td className="p-3 text-sm">{item.unit}</td>
                      <td className="p-3 text-sm text-right">{item.quantity}</td>
                      <td className="p-3 text-sm text-right font-medium">₹{item.budgeted_cost.toFixed(2)}</td>
                      <td className="p-3 text-sm text-zinc-600">{item.trade_name}</td>
                    </tr>
                  ))}
                  <tr className="bg-zinc-50 font-bold">
                    <td colSpan="6" className="p-3 text-sm text-right">Total:</td>
                    <td className="p-3 text-sm text-right">
                      ₹{orderList.reduce((sum, item) => sum + item.budgeted_cost, 0).toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tracking Report Tab */}
        {activeTab === 'tracking' && (
          <div>
            <div className="mb-6">
              <h2 className="text-xl font-semibold tracking-tight text-zinc-800 mb-4">
                Budgeted vs Actual Report
              </h2>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-white border border-zinc-200 p-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">Total Budgeted</p>
                  <p className="text-2xl font-bold text-zinc-900 mt-1">₹{totalBudgeted.toFixed(2)}</p>
                </div>
                <div className="bg-white border border-zinc-200 p-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">Total Actual</p>
                  <p className="text-2xl font-bold text-zinc-900 mt-1">₹{totalActual.toFixed(2)}</p>
                </div>
                <div className={`border-2 p-4 ${totalVariance <= 0 ? 'bg-green-50 border-green-400' : totalVariancePct <= 10 ? 'bg-yellow-50 border-yellow-400' : 'bg-red-50 border-red-400'}`}>
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-600">Variance</p>
                  <p className={`text-2xl font-bold mt-1 ${totalVariance <= 0 ? 'text-green-700' : totalVariancePct <= 10 ? 'text-yellow-700' : 'text-red-700'}`}>
                    {totalVariance >= 0 ? '+' : ''}₹{totalVariance.toFixed(2)} ({totalVariancePct.toFixed(1)}%)
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-zinc-200 overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-100 border-b border-zinc-200">
                  <tr>
                    <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Material</th>
                    <th className="text-right p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Budgeted Qty</th>
                    <th className="text-right p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Received Qty</th>
                    <th className="text-right p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Budgeted Cost</th>
                    <th className="text-right p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Actual Cost</th>
                    <th className="text-right p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Variance</th>
                    <th className="text-center p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {trackingReport.map((item, idx) => (
                    <tr key={idx} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="p-3">
                        <div className="text-sm font-medium">{item.description}</div>
                        <div className="text-xs text-zinc-500">{item.specification} {item.make && `(${item.make})`}</div>
                      </td>
                      <td className="p-3 text-sm text-right">{item.budgeted_quantity} {item.unit}</td>
                      <td className="p-3 text-sm text-right">{item.received_quantity} {item.unit}</td>
                      <td className="p-3 text-sm text-right font-medium">₹{item.budgeted_cost.toFixed(2)}</td>
                      <td className="p-3 text-sm text-right font-medium">₹{item.actual_cost.toFixed(2)}</td>
                      <td className="p-3 text-sm text-right">
                        <span className={item.variance_amount >= 0 ? 'text-red-700' : 'text-green-700'}>
                          {item.variance_amount >= 0 ? '+' : ''}₹{item.variance_amount.toFixed(2)}
                          <br/>
                          <span className="text-xs">({item.variance_percentage.toFixed(1)}%)</span>
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className={`inline-block px-3 py-1 border-2 font-bold text-lg ${getStatusColor(item.status)}`}>
                          {getStatusIcon(item.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Inward History Tab */}
        {activeTab === 'inwards' && (
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-zinc-800 mb-4">
              Material Inward History
            </h2>
            <div className="bg-white border border-zinc-200 overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-100 border-b border-zinc-200">
                  <tr>
                    <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Date</th>
                    <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Material</th>
                    <th className="text-right p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Quantity</th>
                    <th className="text-right p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Cost</th>
                    <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Supplier</th>
                    <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-zinc-600">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {inwards.map((inward, idx) => (
                    <tr key={idx} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="p-3 text-sm">{new Date(inward.received_date).toLocaleDateString()}</td>
                      <td className="p-3">
                        <div className="text-sm font-medium">{inward.material_description}</div>
                        <div className="text-xs text-zinc-500">{inward.specification} {inward.make && `(${inward.make})`}</div>
                      </td>
                      <td className="p-3 text-sm text-right">{inward.quantity} {inward.unit}</td>
                      <td className="p-3 text-sm text-right font-medium">₹{inward.actual_cost.toFixed(2)}</td>
                      <td className="p-3 text-sm">{inward.supplier || '-'}</td>
                      <td className="p-3 text-sm">{inward.invoice_number || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
